import React, { useRef, useState } from "react";
import { X, Download, Share2, Loader2, MapPin } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { sportInfo, positionInfo } from "../lib/api";

/**
 * FIFA-style shareable Player Card.
 * Props:
 *   player: { name, username, picture, city, sports, skill_levels,
 *             reliability_score, matches_played, matches_attended }
 *   onClose()
 */
export default function PlayerCardShare({ player, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!player) return null;

  const sports = (player.sports || []).slice(0, 2); // top 2 sports on card
  const skillLevels = player.skill_levels || {};
  const playerPositions = player.positions || {};
  const primarySport = sports[0];
  const primaryPos = primarySport ? positionInfo(primarySport, playerPositions[primarySport]) : null;
  const reliability = Number(player.reliability_score ?? 100);
  const overall = Math.round(50 + reliability * 0.45 + Math.min(20, (player.matches_played || 0) * 1.5));

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#09090B",
    });
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await generatePng();
      const link = document.createElement("a");
      link.download = `joinplay-${(player.name || "player").toLowerCase().replace(/\s+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Kartu pemain di-download");
    } catch (err) {
      console.warn("[card] download failed:", err?.message);
      toast.error("Gagal generate kartu");
    } finally {
      setBusy(false);
    }
  };

  const handleShareWA = async () => {
    setBusy(true);
    try {
      const dataUrl = await generatePng();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `joinplay-${player.name}.png`, { type: "image/png" });

      // Try Web Share API with file (mobile primarily)
      const text = `Kenalin ${player.name} di JoinPlay 🔥`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "Player Card · JoinPlay" });
        toast.success("Kartu siap di-share");
      } else {
        // Fallback: download + open WA chooser
        const link = document.createElement("a");
        link.download = `joinplay-${player.name}.png`;
        link.href = dataUrl;
        link.click();
        const wa = `https://wa.me/?text=${encodeURIComponent(text + " — gw udah download kartunya, tinggal attach")}`;
        window.open(wa, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.warn("[card] share failed:", err?.message);
        toast.error("Gagal share");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="player-card-share-modal"
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Kartu Pemain</div>
          <button
            onClick={onClose}
            data-testid="card-share-close"
            className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* The card itself — rendered for both display and PNG capture */}
        <div className="px-5">
          <div
            ref={cardRef}
            data-testid="player-card-canvas"
            className="relative aspect-[3/4] rounded-3xl overflow-hidden border border-primary/30"
            style={{
              background:
                "linear-gradient(135deg, #00141A 0%, #001F2E 35%, #002633 65%, #03333d 100%)",
            }}
          >
            {/* Neon corner glows */}
            <div aria-hidden className="absolute -top-16 -right-16 h-56 w-56 rounded-full blur-3xl opacity-50" style={{ background: "#00E5FF" }} />
            <div aria-hidden className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-30" style={{ background: "#A78BFA" }} />

            {/* Diagonal accent stripe */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-20 mix-blend-screen"
              style={{
                background:
                  "repeating-linear-gradient(135deg, transparent 0 22px, rgba(0,229,255,0.18) 22px 24px)",
              }}
            />

            {/* Brand watermark */}
            <div className="absolute top-4 right-4 flex items-center gap-1.5">
              <div className="h-6 w-6 rounded-md bg-primary flex items-center justify-center">
                <span className="font-heading font-black text-black text-xs">J</span>
              </div>
              <span className="font-heading font-black text-sm tracking-tight text-white">JoinPlay</span>
            </div>

            {/* OVR + Position */}
            <div className="absolute top-5 left-5 text-white">
              <div className="font-heading text-5xl font-black leading-none">{overall}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary mt-1">OVR</div>
              {primaryPos && (
                <div className="mt-2 inline-block font-heading text-2xl font-black tracking-tight text-amber-400 leading-none">
                  {primaryPos.short}
                </div>
              )}
              {primarySport && (
                <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-white/90">
                  {sportInfo(primarySport).label}
                </div>
              )}
            </div>

            {/* Portrait */}
            <div className="absolute inset-x-0 top-[18%] flex justify-center">
              <div className="h-36 w-36 rounded-full overflow-hidden border-4 border-primary/60 shadow-[0_0_60px_rgba(0,229,255,0.45)] bg-secondary">
                {player.picture ? (
                  <img src={player.picture} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-heading text-5xl font-black text-white">
                    {(player.name || "?")[0]?.toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Name & meta */}
            <div className="absolute inset-x-0 top-[58%] text-center px-4 text-white">
              <div className="font-heading text-2xl font-black tracking-tight uppercase leading-tight">
                {player.name}
              </div>
              {player.city && (
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/70">
                  <MapPin className="h-3 w-3" /> {player.city}
                </div>
              )}
            </div>

            {/* Stat strip */}
            <div className="absolute inset-x-5 bottom-20 grid grid-cols-3 gap-2">
              <CardStat label="MTC" value={player.matches_played ?? 0} />
              <CardStat label="ATT" value={player.matches_attended ?? 0} />
              <CardStat label="REL" value={`${reliability.toFixed(0)}`} />
            </div>

            {/* Sports chips */}
            <div className="absolute inset-x-5 bottom-5 flex flex-wrap items-center gap-1.5">
              {sports.length > 0 ? sports.map((sid) => {
                const s = sportInfo(sid);
                const lvl = (skillLevels[sid] || "intermediate").slice(0, 3).toUpperCase();
                const pos = positionInfo(sid, playerPositions[sid]);
                return (
                  <div key={sid} className="bg-black/35 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                    <span className="text-sm">{s.icon}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">{s.label}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-1.5 py-0.5 rounded-full">{lvl}</span>
                    {pos && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400 bg-amber-400/15 px-1.5 py-0.5 rounded-full">{pos.short}</span>}
                  </div>
                );
              }) : (
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Sport belum di-set</div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] grid grid-cols-2 gap-2">
          <button
            onClick={handleDownload}
            disabled={busy}
            data-testid="card-download-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-secondary border border-border font-bold disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download
          </button>
          <button
            onClick={handleShareWA}
            disabled={busy}
            data-testid="card-share-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-primary text-primary-foreground font-bold glow-cyan active:scale-95 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            Share
          </button>
        </div>
      </div>
    </div>
  );
}

function CardStat({ label, value }) {
  return (
    <div className="bg-black/35 backdrop-blur-sm border border-white/15 rounded-xl py-2 text-center">
      <div className="font-heading text-xl font-black text-white leading-none">{value}</div>
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-primary mt-1">{label}</div>
    </div>
  );
}
